import { MapPin, Battery, Bug, BellRing, Rocket, Check, Loader2, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useDriverTheme } from '@/hooks/useDriverTheme';
import { useLanguage } from '@/hooks/useLanguage';
import {
  usePermissionsGate,
  type PermissionKey,
  type PermissionsState,
} from '@/hooks/usePermissionsGate';
import BackgroundLocationRationaleSheet from '@/components/BackgroundLocationRationaleSheet';

/**
 * Full-screen Required Permissions page.
 * Compact single-screen layout — all four cards fit on a 390×844 viewport
 * without scrolling. Replaces the entire app surface (including login)
 * until every required permission is satisfied.
 */

interface CardConfig {
  key: PermissionKey;
  titleKey: 'perm_location_title' | 'perm_battery_title' | 'perm_developer_title' | 'perm_fullscreen_title' | 'perm_autostart_title';
  bodyKey: 'perm_location_body' | 'perm_battery_body' | 'perm_developer_body' | 'perm_fullscreen_body' | 'perm_autostart_body';
  icon: LucideIcon;
}

const CARDS: CardConfig[] = [
  { key: 'location', titleKey: 'perm_location_title', bodyKey: 'perm_location_body', icon: MapPin },
  { key: 'battery', titleKey: 'perm_battery_title', bodyKey: 'perm_battery_body', icon: Battery },
  { key: 'developer', titleKey: 'perm_developer_title', bodyKey: 'perm_developer_body', icon: Bug },
  { key: 'fullScreen', titleKey: 'perm_fullscreen_title', bodyKey: 'perm_fullscreen_body', icon: BellRing },
  { key: 'oemAutostart', titleKey: 'perm_autostart_title', bodyKey: 'perm_autostart_body', icon: Rocket },
];

function isGranted(perms: PermissionsState, key: PermissionKey): boolean {
  return perms[key] === 'granted';
}

export default function RequiredPermissionsScreen() {
  const { tokens, isDark } = useDriverTheme();
  const { t } = useLanguage();
  const { perms, request } = usePermissionsGate();
  const [busy, setBusy] = useState<PermissionKey | null>(null);

  const handleClick = async (key: PermissionKey) => {
    if (busy) return;
    setBusy(key);
    try {
      await request(key);
    } finally {
      setBusy(null);
    }
  };

  const cardBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const iconBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
  const cardBg = isDark ? '#241c17' : '#ffffff';

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col"
      style={{
        background: isDark ? '#1a1410' : '#f3f3f3',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div
        className="px-4 py-2.5 text-center text-sm font-bold border-b shrink-0"
        style={{
          background: tokens.bgPage,
          color: tokens.textPrimary,
          borderColor: cardBorder,
        }}
      >
        {t('required_permission_header')}
      </div>

      <div className="flex flex-col gap-2.5 px-3 py-3">
        {CARDS.map((card) => {
          const granted = isGranted(perms, card.key);
          const Icon = card.icon;
          return (
            <div
              key={card.key}
              className="rounded-xl px-3 py-3 shadow-sm flex items-center gap-3"
              style={{
                background: cardBg,
                border: `1px solid ${cardBorder}`,
              }}
            >
              <div
                className="flex h-9 w-9 items-center justify-center rounded-full shrink-0"
                style={{ background: iconBg }}
              >
                <Icon className="h-4 w-4" style={{ color: tokens.textPrimary }} />
              </div>

              <div className="flex-1 min-w-0">
                <h3
                  className="text-sm font-bold leading-tight"
                  style={{ color: tokens.textPrimary }}
                >
                  {t(card.titleKey)}
                </h3>
                <p
                  className="text-[11px] leading-snug mt-0.5"
                  style={{ color: tokens.textSecondary }}
                >
                  {t(card.bodyKey)}
                </p>
              </div>

              <div className="shrink-0">
                {granted ? (
                  <div
                    className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full"
                    style={{
                      color: '#16a34a',
                      background: isDark ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.10)',
                    }}
                  >
                    <Check className="h-3 w-3" strokeWidth={3} />
                    {t('perm_granted')}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleClick(card.key)}
                    disabled={busy !== null}
                    className="text-[11px] font-bold tracking-wide px-3 py-1.5 rounded-full flex items-center gap-1 disabled:opacity-50"
                    style={{
                      color: '#ffffff',
                      background: '#dc2626',
                    }}
                  >
                    {busy === card.key && <Loader2 className="h-3 w-3 animate-spin" />}
                    {t('perm_allow')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <BackgroundLocationRationaleSheet />
    </div>
  );
}
